<script>
import axios from 'axios';

export default {
    props: {
        selectedCompanyId: {
            type: Number,
            required: true,
        },

        isInReportingFlow: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            loading: false,

            carbonOptions: null,
            facilities: null,
            inputYears: null,
            currencyCode: null,
        }
    },

    computed: {
        reportingYear() {
            return new Date().getFullYear() -1;
        },

        options() {
            const baseYearSelection = [
                { val: 'current', text: this.$t('carbon-options.options.current'), visible: true },
                { val: 'auto', text: this.$t('carbon-options.options.auto'), visible: true },
                { val: 'manual', text: this.$t('carbon-options.options.manual'), visible: true },
            ];

            const calculationType = [
                { val: 'iso', text: this.$t('carbon-options.options.iso') },
                { val: 'defra', text: this.$t('carbon-options.options.defra') },
            ];

            const reportingFrequency = [
                { val: 'yearly', text: this.$t('carbon-options.options.yearly') },
                { val: 'quarterly', text: this.$t('carbon-options.options.quarterly') },
            ];
            
            const hasRemovalProcess = [
                { val: true, text: this.$t('carbon-options.options.removal_process_true') },
                { val: false, text: this.$t('carbon-options.options.removal_process_false') },
            ];

            const calculationMethod = [
                { val: 'financial', text: this.$t('carbon-options.options.financial-operation') },
                { val: 'operational', text: this.$t('carbon-options.options.operational-operation') },
            ];

            const designatedConfidenceLevel = [
                { val: 'reasonable', text: this.$t('carbon-options.options.reasonable-confidence') },
                { val: 'limited', text: this.$t('carbon-options.options.limited-confidence') },
            ];

            const emissionDensityCalculation = [
                { val: 'revenue', text: this.$t('carbon-options.options.revenue-calculation') },
                { val: 'custom', text: this.$t('carbon-options.options.custom-calculation') },
            ];

            if (this.inputYears === null || this.inputYears === undefined || this.inputYears.length === 0) {
                baseYearSelection[1].visible = false;
            }

            return {
                base_year_selection: baseYearSelection,
                calculation_type: calculationType,
                reporting_frequency: reportingFrequency,
                has_removal_process: hasRemovalProcess,
                calculation_method: calculationMethod,
                designated_confidence_level: designatedConfidenceLevel,
                emission_density_calculation: emissionDensityCalculation
            };
        },

        nullable_keys() {
            return [
                'entities_out_of_bound',
                'reason_for_method_change',
                'exclusion_criteria'
            ];
        }
    },

    mounted() {
        this._getCarbonOptions();
    },

    methods: {
        getCarbonOptions() {
            return axios.get(
                `/api/company/companyprofile/carbon-options`,
                { 
                    withCredentials: true, 
                    params: {
                        year: this.reportingYear
                    } 
                }
            );
        },

        updateCarbonOptions(params) {
            return axios.post(
                `/api/company/companyprofile/update-carbon-option/${params.option}/${params.id}`,
                params.body,
                { withCredentials: true }
            );
        },

        _getCarbonOptions() {
            this.loading = true;
            this.getCarbonOptions()
                .then(res => {
                    this.carbonOptions = res.data.carbon_options;
                    this.facilities = res.data.facilities;
                    this.inputYears = res.data.input_years;
                    this.currencyCode = res.data.currency_code;
                })
                .finally(() => {
                    this.loading = false;
                })
        },

        _updateCarbonOption(key, options = undefined) {
            const value = this.carbonOptions[key];

            let body_value = null;
            if (!this.nullable_keys.includes(key) && (value === null || (String(value).trim().length === 0)))
                return this.$notify('error', this.$t('carbon-options.errors.cant-be-empty'), true);

            if (options !== undefined) {
                if (options.manual_value_type !== undefined && options.manual_value_index !== undefined) {
                    const v = this.carbonOptions[`manual_${options.manual_value_type}_values`][options.manual_value_index];
                    if (isNaN(v) || Number(v) < 0 || String(v).trim().length === 0) {
                        return this.$notify('error', this.$t('carbon-options.errors.enforce-numeric'), true);
                    }
                }

                body_value = this.carbonOptions[`manual_${options.manual_value_type}_values`];
                key = `manual_${options.manual_value_type}_values`;
            } else {
                body_value = value
            }

            if (typeof body_value === 'string') body_value.trim();

            const params = {
                option: key,
                id: this.carbonOptions.id,
                body: {
                    value: body_value
                }
            };

            this.updateCarbonOptions(params)
                .then(() => {
                    if (params.option === 'base_year' && !this.carbonOptions.base_year && (params.body.value === 'auto' || params.body.value === 'manual'))
                        this.base_year === this.inputYears && this.inputYears.length > 0 ? this.inputYears[0] : this.reportingYear;

                    this.$notify('success', '', true);
                })
        },

        handleFacilityCheck(e, facility) {
            const checked = e.target.checked;
            if (checked === false) {
                this.carbonOptions.included_facilities = this.carbonOptions.included_facilities.filter(x => x !== facility.facility_id);
            } else {
                this.carbonOptions.included_facilities.push(facility.facility_id);
            }

            this._updateCarbonOption('included_facilities');
        }
    }
}
</script>

<template>
    <div class="flex-all-centered-col" 
        style="justify-content: flex-start; align-items: flex-start"
    >
        <div v-if="carbonOptions !== null && !loading" 
            class="option-group"
        >
            <div class="option-row">
                <label for="base_year_selection">
                    {{ $t('carbon-options.fields.base_year_selection') }} :
                </label>
                <select id="base_year_selection"
                    v-model="carbonOptions.base_year_selection"
                    @change="_updateCarbonOption('base_year_selection')"
                >
                    <option v-for="option in options.base_year_selection.filter(x => x.visible)"
                        :value="option.val"
                        :key="option.val"
                    >
                        {{ option.text }}
                        <span v-if="option.val === 'current'">
                            ({{ reportingYear }})
                        </span>
                    </option>
                </select>
            </div>

            <div v-if="carbonOptions.base_year_selection === 'auto'" 
                class="option-row"
            >
                <label for="base_year">
                    {{ $t('carbon-options.fields.base_year') }} :
                </label>
                <select id="base_year"
                    v-model="carbonOptions.base_year"
                    @change="_updateCarbonOption('base_year')"
                >
                    <option v-for="year in inputYears"
                        :value="year"
                        :key="year"
                    >
                        {{ year }}
                    </option>
                </select>
            </div>

            <div v-if="carbonOptions.base_year_selection === 'manual'"
                class="option-row"
            >
                <label for="calculation_type">
                    {{ $t('carbon-options.fields.base_year') }} :
                </label>
                <input id="base_year"
                    type="number"
                    v-model="carbonOptions.base_year"
                    @change="_updateCarbonOption('base_year')"
                >
            </div>

            <div v-if="carbonOptions.base_year_selection === 'manual'"
                class="option-row"
            >
                <label for="calculation_type">
                    {{ $t('carbon-options.fields.calculation_type') }} :
                </label>
                <select id="calculation_type"
                    v-model="carbonOptions.calculation_type"
                    @change="_updateCarbonOption('calculation_type')"
                >
                    <option v-for="option in options.calculation_type"
                        :value="option.val"
                        :key="option.val"
                    >
                        {{ option.text }}
                    </option>
                </select>
            </div>
            
            <div v-if="carbonOptions.base_year_selection === 'manual'"
                class="option-row"
            >
                <label :for="`manual_${carbonOptions.calculation_type}_values`">
                    {{ $t('carbon-options.fields.manual_values') }} :
                </label>
                
                <div class="value-group-wrapper">
                    <div v-for="(_, idx) in carbonOptions[`manual_${carbonOptions.calculation_type}_values`]"
                        :key="idx"
                        class="value-group"
                    >
                        <div style="border-bottom: thin solid lightgray; margin-bottom: .5em;">
                            {{ $t(`carbon-options.fields.manual_value_label.${carbonOptions.calculation_type}`) }} {{ idx + 1 }}
                        </div>

                        <input type="number" v-model="carbonOptions[`manual_${carbonOptions.calculation_type}_values`][idx]"
                            @change="_updateCarbonOption(`manual_value`, {
                                manual_value_index: idx,
                                manual_value_type: carbonOptions.calculation_type,
                            })"
                        >
                    </div>
                </div>
            </div>

            <div v-if="isInReportingFlow" class="option-row">
                <label for="activity_sector">
                    {{ $t('carbon-options.fields.activity_sector') }} :
                </label>
                <input id="activity_sector"
                    v-model="carbonOptions.activity_sector"
                    @change="_updateCarbonOption('activity_sector')"
                    type="text"
                >
            </div>
            
            <div v-if="isInReportingFlow" class="option-row">
                <label for="target_audience"
                    v-sust-tooltip="{
                        text: `
                            ${$t('carbon-options.descriptions.target_audience')}
                            \n
                            ${$t('carbon-options.descriptions.can-be-multiple')}
                        `
                    }"
                >
                    <i class="fa-solid fa-circle-info"></i>
                    {{ $t('carbon-options.fields.target_audience') }} :
                </label>
                <input id="target_audience"
                    v-model="carbonOptions.target_audience"
                    @change="_updateCarbonOption('target_audience')"
                    type="text"
                >
            </div>

            <div class="option-row">
                <label for="calculation_method">
                    {{ $t('carbon-options.fields.calculation_method') }} :
                </label>
                <select id="calculation_method"
                    v-model="carbonOptions.calculation_method"
                    @change="_updateCarbonOption('calculation_method')"
                >
                    <option v-for="option in options.calculation_method"
                        :value="option.val"
                        :key="option.val"
                    >
                        {{ option.text }}
                    </option>
                </select>
            </div>

            <div class="option-row">
                <label for="reason_for_method_change">
                    {{ $t('carbon-options.fields.reason_for_method_change') }} :
                </label>
                <input id="reason_for_method_change"
                    v-model="carbonOptions.reason_for_method_change"
                    @change="_updateCarbonOption('reason_for_method_change')"
                    type="text"
                >
            </div>

            <div class="option-row">
                <label for="reporting_frequency">
                    {{ $t('carbon-options.fields.reporting_frequency') }} :
                </label>
                <select id="reporting_frequency"
                    v-model="carbonOptions.reporting_frequency"
                    @change="_updateCarbonOption('reporting_frequency')"
                >
                    <option v-for="option in options.reporting_frequency"
                        :value="option.val"
                        :key="option.val"
                    >
                        {{ option.text }}
                    </option>
                </select>
            </div>
            
            <div class="option-row">
                <label for="head_department_for_reporting" can-be-multiple>
                    {{ $t('carbon-options.fields.head_department_for_reporting') }} :
                </label>
                <input id="head_department_for_reporting"
                    v-model="carbonOptions.head_department_for_reporting"
                    @change="_updateCarbonOption('head_department_for_reporting')"
                    type="text"
                >
            </div>
            
            <div class="option-row">
                <label for="head_department_for_auditing" can-be-multiple>
                    {{ $t('carbon-options.fields.head_department_for_auditing') }} :
                </label>
                <input id="head_department_for_auditing"
                    v-model="carbonOptions.head_department_for_auditing"
                    @change="_updateCarbonOption('head_department_for_auditing')"
                    type="text"
                >
            </div>
            
            <div class="option-row">
                <label for="auditing_method"
                    v-sust-tooltip="{
                        text: $t('carbon-options.descriptions.auditing_method')
                    }"
                >
                    <i class="fa-solid fa-circle-info"></i>
                    {{ $t('carbon-options.fields.auditing_method') }} :
                </label>
                <input id="auditing_method"
                    v-model="carbonOptions.auditing_method"
                    @change="_updateCarbonOption('auditing_method')"
                    type="text"
                >
            </div>
            
            <div class="option-row">
                <label for="require_internal_audit"
                    v-sust-tooltip="{
                        text: $t('carbon-options.internal-audit-warning')
                    }"
                >
                    <i class="fa-solid fa-circle-info"></i>
                    {{ $t('carbon-options.fields.require_internal_audit') }} :
                </label>
                <input id="require_internal_audit"
                    v-model="carbonOptions.require_internal_audit"
                    @change="_updateCarbonOption('require_internal_audit')"
                    type="checkbox"
                >
            </div>
            
            <div class="option-row">
                <label for="included_facilities">
                    {{ $t('carbon-options.fields.included_facilities') }} :
                </label>

                <div class="option-group"
                    style="min-width: 82.5% !important; 
                        max-width: 82.5% !important; 
                        width: 82.5% !important;
                        padding-left: .5em;
                    "
                >
                    <div v-for="facility in facilities"
                        :key="facility.facility_id"
                        class="option-row"
                    >
                        <input style="min-width: fit-content !important; 
                                max-width: fit-content !important; 
                                width: fit-content !important;
                                margin-right: .5em;
                            "
                            :checked="carbonOptions.included_facilities.includes(facility.facility_id)"
                            @change="handleFacilityCheck($event, facility)"
                            type="checkbox"
                            :id="`facility_${facility.facility_name}`"
                        >

                        <label :for="`facility_${facility.facility_name}`" 
                            style="min-width: fit-content !important; max-width: fit-content !important; width: fit-content !important;"
                        >
                            {{ facility.facility_name }} ({{ facility.province }})
                        </label>
                    </div>
                </div>
            </div>

            <div class="option-row">
                <label for="entities_out_of_bound" can-be-multiple>
                    {{ $t('carbon-options.fields.entities_out_of_bound') }} :
                </label>
                <input id="entities_out_of_bound"
                    v-model="carbonOptions.entities_out_of_bound"
                    @change="_updateCarbonOption('entities_out_of_bound')"
                    type="text"
                >
            </div>

            <div class="option-row">
                <label for="has_removal_process">
                    {{ $t('carbon-options.fields.has_removal_process') }} :
                </label>
                <select id="has_removal_process"
                    v-model="carbonOptions.has_removal_process"
                    @change="_updateCarbonOption('has_removal_process')"
                >
                    <option v-for="option in options.has_removal_process"
                        :value="option.val"
                        :key="option.val"
                    >
                        {{ option.text }}
                    </option>
                </select>
            </div>

            <div v-if="carbonOptions.has_removal_process" 
                class="option-row"
            >
                <label for="removal_process">
                    {{ $t('carbon-options.fields.removal_process') }} :
                </label>
                <input id="removal_process"
                    v-model="carbonOptions.removal_process"
                    @change="_updateCarbonOption('removal_process')"
                    type="text"
                >
            </div>

            <div class="option-row">
                <label for="emission_density_calculation">
                    {{ $t('carbon-options.fields.emission_density_calculation') }} :
                </label>
                <select id="emission_density_calculation"
                    v-model="carbonOptions.emission_density_calculation"
                    @change="_updateCarbonOption('emission_density_calculation')"
                >
                    <option v-for="option in options.emission_density_calculation"
                        :value="option.val"
                        :key="option.val"
                    >
                        {{ option.text }}
                    </option>
                </select>
            </div>

            <div v-if="carbonOptions.emission_density_calculation === 'revenue'" class="option-row">
                <label for="revenue">
                    {{ $t(`carbon-options.options.${carbonOptions.reporting_frequency}`) }} {{ $t('carbon-options.fields.revenue') }} 
                    ({{ reportingYear }}) {{ currencyCode ? `(${currencyCode})` : '' }} :
                </label>
                <input id="revenue"
                    v-model="carbonOptions.revenue"
                    @change="_updateCarbonOption('revenue')"
                    type="number"
                >
            </div>
            
            <div v-if="carbonOptions.emission_density_calculation === 'custom'" class="option-row">
                <label for="emission_density_unit">
                    {{ $t('carbon-options.fields.emission_density_unit') }} :
                </label>
                <input id="emission_density_unit"
                    v-model="carbonOptions.emission_density_unit"
                    @change="_updateCarbonOption('emission_density_unit')"
                    type="text"
                >
            </div>
            
            <div v-if="carbonOptions.emission_density_calculation === 'custom'" class="option-row">
                <label for="emission_density_value">
                    {{ $t('carbon-options.fields.emission_density_value') }} :
                </label>
                <input id="emission_density_value"
                    v-model="carbonOptions.emission_density_value"
                    @change="_updateCarbonOption('emission_density_value')"
                    type="number"
                >
            </div>

            <div class="option-row">
                <label for="designated_confidence_level">
                    {{ $t('carbon-options.fields.designated_confidence_level') }} :
                </label>
                <select id="designated_confidence_level"
                    v-model="carbonOptions.designated_confidence_level"
                    @change="_updateCarbonOption('designated_confidence_level')"
                >
                    <option v-for="option in options.designated_confidence_level"
                        :value="option.val"
                        :key="option.val"
                    >
                        {{ option.text }}
                    </option>
                </select>
            </div>

            <div class="option-row">
                <label for="exclusion_criteria">
                    {{ $t('carbon-options.fields.exclusion_criteria') }} :
                </label>
                <input id="exclusion_criteria"
                    v-model="carbonOptions.exclusion_criteria"
                    @change="_updateCarbonOption('exclusion_criteria')"
                    type="text"
                >
            </div>
        </div>

        <div v-if="isInReportingFlow" class="flex-all-centered-row"
            style="justify-content: flex-end; margin-top: 1em;"
        >
            <button class="sust-btn sust-btn-green" @click="$emit('done')">
                {{ $t('misc.ok') }}
            </button>
        </div>

        <div v-if="loading" class="flex-all-centered-row">
            <loading-spinner fontSize="2em" />
        </div>
    </div>
</template>

<style lang='scss' scoped>
    .option-group {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
    }
    
    .option-row {
        width: 100%;
        display: flex;
        justify-content: flex-start;
        align-items: center;

        margin-top: 1em;

        > i {
            min-width: 2.5% !important;
            max-width: 2.5% !important;
            width: 2.5% !important;
        }

        > label {
            min-width: 15% !important;
            max-width: 15% !important;
            width: 15% !important;
        }

        > input, select, .value-group-wrapper {
            width: 100%;
        }
    }

    label {
        padding: 0 .75em 0 0;
        text-align: right;
        font-weight: bold;
        line-height: 1.2rem;
        margin: 0 !important;

        pointer-events: all !important;
    }

    .fa-circle-info {
        color: var(--sustainfinity-blue);
        transition: color 250ms;
        
        &:hover {
            color: var(--sustainfinity-navy);
        }

        &[can-be-multiple] {
            color: var(--sustainfinity-yellow) !important;

            &:hover {
                color: var(--sustainfinity-yellow--hover) !important;
            }
        }
    }

    input, select {
        outline: 0 !important;
        border: 0;
        border-bottom: thin solid lightgray;
        background-color: transparent;

        transition: border-bottom-color 250ms;
        &:focus {
            border-bottom-color: black;
        }

        &#require_internal_audit {
            min-width: 25px !important;
            max-width: 25px !important;
            width: 25px !important;
        }
    }

    .value-group-wrapper {
        display: flex;
        justify-content: flex-start;
        align-items: center;

        > .value-group {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;

            > input {
                text-align: center;
            }
        }
    }
</style>
